import { UserServiceClient } from 'fitbeat.api-clients';
import { UserProfile } from 'fitbeat.gym-core';
import { IPaymentSetupData, ISubscriptionPaymentSummary } from 'fitbeat.models';
import { PaywallPage } from '../../auth';
import { IAppStore } from '../../HomeWorkouts';
import { analyicsForAction, IAnalyticsForAction } from '../../services/google-analytics/analyicsForAction';
import { Logger } from '../../utils';
import { createUnauthenticatedUserServiceClient, createUserServiceClient } from '../../utils/apiClientFactory';

export const types = {
  HAVE_COUPON_PRESSED: 'HAVE_COUPON_PRESSED',
  APPLY_COUPON_REQUEST: 'APPLY_COUPON_REQUEST',
  APPLY_COUPON_SUCCESS: 'APPLY_COUPON_SUCCESS',
  APPLY_COUPON_FAILURE: 'APPLY_COUPON_FAILURE',
  APPLY_COUPON_PRESSED: 'APPLY_COUPON_PRESSED',
  SUBSCRIPTION_SUMMARY_AND_PAYMENT_DATA_LOADED: 'SUBSCRIPTION_SUMMARY_AND_PAYMENT_DATA_LOADED',
  REMOVE_COUPON_REQUEST: 'REMOVE_COUPON_REQUEST',
  REMOVE_COUPON_SUCCESS: 'REMOVE_COUPON_SUCCESS',
  REMOVE_COUPON_FAILURE: 'REMOVE_COUPON_FAILURE',
  PAYWALL_MODAL_OPEN: 'PAYWALL_MODAL_OPEN',
  PAYWALL_MODAL_CLOSE: 'PAYWALL_MODAL_CLOSE',
};

interface ISubscriptionSummaryAndPaymentSetup {
  paymentSubscriptionSummary: ISubscriptionPaymentSummary;
  paymentSetupData: IPaymentSetupData | null;
}

const getSubscriptionSummary = (
  coupon: string,
  uid: string,
  state: IAppStore,
  userServiceClient: UserServiceClient,
) => {
  const {
    planId,
    onceoff,
    pendingPlanId,
    pendingCoupon,
    stripeCustomerId,
  } = state.deepLinkParamaters;
  return userServiceClient.getSubscriptionPaymentSummary(
    uid,
    planId,
    coupon,
    onceoff,
    pendingPlanId,
    pendingCoupon,
    stripeCustomerId,
  );
};

const getPaymentSetupData = (coupon: string, uid: string, state: IAppStore, userServiceClient: UserServiceClient) => {
  const {
    planId,
    onceoff,
    pendingPlanId,
    pendingCoupon,
  } = state.deepLinkParamaters;
  return userServiceClient.createSetupIntentForCustomer(uid, coupon, onceoff, planId, pendingPlanId, pendingCoupon);
};

export const applyCouponPressed = (couponCode: string, planId: string | undefined, userId: string) => (
  async (
    dispatch: (action: IApplyCouponRequest | IApplyCouponSuccess | IApplyCouponFailed) => void,
    getState: () => IAppStore,
  ) => {
    dispatch({
      type: types.APPLY_COUPON_REQUEST,
      payload: { couponToApply: couponCode },
      ...analyicsForAction(types.APPLY_COUPON_PRESSED),
    });
    try {
      if (!planId) {
        throw new Error('Action applyCouponPressed called without planId');
      }
      const userServiceClient = createUserServiceClient();
      const { valid } = await userServiceClient.couponVerify(couponCode, planId);

      if (valid) {
        const subscriptionData = await getSubscriptionSummaryAndPaymentSetupData(couponCode, getState, userId);
        dispatch({
          type: types.APPLY_COUPON_SUCCESS,
          payload: subscriptionData,
        });
      } else {
        dispatch({ type: types.APPLY_COUPON_FAILURE });
      }
    } catch (e) {
      Logger.notify(e);
      dispatch({ type: types.APPLY_COUPON_FAILURE });
    }
  });

export const removeCouponPressed = (userId: string) => (
  async (
    dispatch: (action: IRemoveCouponRequest | IRemoveCouponSuccess | IRemoveCouponFailure) => void,
    getState: () => IAppStore,
  ) => {
    dispatch({
      type: types.REMOVE_COUPON_REQUEST,
      ...analyicsForAction(types.REMOVE_COUPON_REQUEST),
    });
    try {
      const subscriptionData = await getSubscriptionSummaryAndPaymentSetupData('', getState, userId);
      dispatch({
        type: types.REMOVE_COUPON_SUCCESS,
        payload: subscriptionData,
      });
    } catch (e) {
      dispatch({ type: types.REMOVE_COUPON_FAILURE });
    }
  });

async function getSubscriptionSummaryAndPaymentSetupData(coupon: string, getState: () => IAppStore, userId):
  Promise<ISubscriptionSummaryAndPaymentSetup> {
  const userServiceClient = createUnauthenticatedUserServiceClient();
  const state = getState();
  const paymentSubscriptionSummary = await getSubscriptionSummary(coupon, userId, state, userServiceClient);

  let paymentSetupData = null;
  if (paymentSubscriptionSummary.paymentMethodRequired) {
    paymentSetupData = await getPaymentSetupData(coupon, userId, state, userServiceClient);
  }

  return { paymentSubscriptionSummary, paymentSetupData };
}

export const haveCouponPressed = (): IHaveCouponPressed => ({
  type: types.HAVE_COUPON_PRESSED,
  ...analyicsForAction(types.HAVE_COUPON_PRESSED),
});

// TODO: refactor PreLoadedPlanDetails.js to fully use redux.
//  This will enable this action to change to a user driven action.
export const setSubscriptionSummaryAndPaymentSetupData =
  (paymentSubscriptionSummary, paymentSetupData): ISetSubscriptionSummaryAndPaymentSetupData => ({
    type: types.SUBSCRIPTION_SUMMARY_AND_PAYMENT_DATA_LOADED,
    payload: {
      paymentSubscriptionSummary,
      paymentSetupData,
    },
  });

interface IHaveCouponPressed extends IAnalyticsForAction {
  type: typeof types.HAVE_COUPON_PRESSED;
}

interface IApplyCouponRequest extends IAnalyticsForAction {
  type: typeof types.APPLY_COUPON_REQUEST;
  payload: {
    couponToApply: string;
  };
}

interface IApplyCouponFailed extends IAnalyticsForAction {
  type: typeof types.APPLY_COUPON_FAILURE;
}

interface IApplyCouponSuccess extends IAnalyticsForAction {
  type: typeof types.APPLY_COUPON_SUCCESS;
  payload: ISubscriptionSummaryAndPaymentSetup;
}

interface IRemoveCouponRequest extends IAnalyticsForAction {
  type: typeof types.REMOVE_COUPON_REQUEST;
}

interface IRemoveCouponSuccess {
  type: typeof types.REMOVE_COUPON_SUCCESS;
  payload: ISubscriptionSummaryAndPaymentSetup;
}

interface IRemoveCouponFailure {
  type: typeof types.REMOVE_COUPON_FAILURE;
}

interface ISetSubscriptionSummaryAndPaymentSetupData {
  type: typeof types.SUBSCRIPTION_SUMMARY_AND_PAYMENT_DATA_LOADED;
  payload: ISubscriptionSummaryAndPaymentSetup;
}

export const openPaywallModalAction = (
  paywallPage: PaywallPage,
  paywallOnSuccessCallback?: () => void,
  paywallOnCloseCallback?: () => void,
): IOpenPaywallModal => ({
  type: types.PAYWALL_MODAL_OPEN,
  payload: {
    paywallOnCloseCallback,
    paywallOnSuccessCallback,
    paywallPage,
  },
});

interface IOpenPaywallModal {
  type: typeof types.PAYWALL_MODAL_OPEN;
  payload: {
    paywallOnCloseCallback?: () => void;
    paywallOnSuccessCallback?: () => void;
    paywallPage: PaywallPage;
  };
}

export const closePaywallModalAction = (): IClosePaywallModal => ({
  type: types.PAYWALL_MODAL_CLOSE,
});

interface IClosePaywallModal {
  type: typeof types.PAYWALL_MODAL_CLOSE;
}

export type PaymentActionTypes =
  IApplyCouponRequest |
  IHaveCouponPressed |
  IApplyCouponSuccess |
  IApplyCouponFailed |
  ISetSubscriptionSummaryAndPaymentSetupData |
  IRemoveCouponRequest |
  IRemoveCouponSuccess |
  IRemoveCouponFailure |
  IOpenPaywallModal |
  IClosePaywallModal;
